<template>
  <div>
    <!-- SHOWING LOGIN FORM ERRORS -->
    <div
      v-if="formErrors.length"
      class="mb-4 d-flex justify-content-center align-items-center gap-2 flex-column"
    >
      <div
        v-for="error in formErrors"
        :key="error"
        class="w-100 mb-0 alert alert-danger text-center"
        role="alert"
        >{{ error }}</div
      >
    </div>
    <!-- END OF SHOWING LOGIN FORM ERRORS -->
    <b-form
      key="formState"
      id="password-login-form"
      ref="loginForm"
      @submit="handleLoginFormSubmit"
      @reset="handleLoginFormReset"
    >
      <b-form-group
        :id="`${fieldInfo.login.name}-${formGroupSuffix}`"
        :label="fieldInfo.login.label"
        :label-sr-only="true"
        :label-for="`${fieldInfo.login.name}-${inputIdSuffix}`"
        class="mt-0 mb-2"
        :state="loginFieldErrors.length ? false : null"
        :invalid-feedback="loginFieldErrors.length ? loginFieldErrors.join(', ') : ''"
      >
        <b-input-group
          :id="`${fieldInfo.login.name}-${inputGroupSuffix}`"
          size="lg"
          class="mt-0"
        >
          <template #prepend>
            <b-input-group-text><b-icon :icon="fieldInfo.login.icon"></b-icon></b-input-group-text>
          </template>
          <b-form-input
            :id="`${fieldInfo.login.name}-${inputIdSuffix}`"
            v-model="formData.email"
            trim
            required
            :type="fieldInfo.login.type"
            :name="fieldInfo.login.name"
            :placeholder="fieldInfo.login.placeholder"
            :autocomplete="fieldInfo.login.autocomplete"
            :aria-label="fieldInfo.login.label"
            :aria-placeholder="fieldInfo.login.placeholder"
            :state="loginFieldErrors.length ? false : null"
          >
          </b-form-input>
        </b-input-group>
      </b-form-group>
      <b-form-group
        :id="`${fieldInfo.password.name}-${formGroupSuffix}`"
        :label="fieldInfo.password.label"
        :label-sr-only="true"
        :label-for="`${fieldInfo.password.name}-${inputIdSuffix}`"
        class="mt-0 mb-2"
        :state="passwordFieldErrors.length ? false : null"
        :invalid-feedback="passwordFieldErrors.length ? passwordFieldErrors.join(', ') : ''"
      >
        <b-input-group
          :id="`${fieldInfo.password.name}-${inputGroupSuffix}`"
          size="lg"
          class="mt-0"
        >
          <template #prepend>
            <b-input-group-text><b-icon :icon="fieldInfo.password.icon"></b-icon></b-input-group-text>
          </template>
          <b-form-input
            :id="`${fieldInfo.password.name}-${inputIdSuffix}`"
            v-model="formData.password"
            required
            :type="fieldInfo.password.type"
            :name="fieldInfo.password.name"
            :placeholder="fieldInfo.password.placeholder"
            :autocomplete="fieldInfo.password.autocomplete"
            :aria-label="fieldInfo.password.label"
            :aria-placeholder="fieldInfo.password.placeholder"
            :state="passwordFieldErrors.length ? false : null"
          >
          </b-form-input>
        </b-input-group>
      </b-form-group>
      <!-- <b-form-input
        :value="csrfToken"
        hidden
        required
        type="text"
        name="csrfmiddlewaretoken"
      ></b-form-input> -->  <!-- TODO: do we actually need a hidden field for this?  or just add it when POSTing -->
      <b-button
        size="lg"
        class="w-100"
        type="submit"
        variant="primary"
        >Sign in</b-button
      >
    </b-form>

    <div v-if="passwordResetUrl" class="text-center mt-2">
      <b-link
        class=""
        :href="passwordResetUrl"
        >Forgot Password?</b-link>
    </div>
  </div>
</template>

<script>
  import { suppliosApi } from '@/js/api-service';
  import { splUrlMapMixin } from '@/js/custom-vue-mixins';
  import axios from 'axios';

  export default {
    name: 'PasswordLoginForm',
    components: {},
    mixins: [splUrlMapMixin],
    props: {
      // csrfToken: {  // NO LONGER NEEDED - we WANT to use the CSRF token from the cookie, not the page - see other comments
      //   type: String,
      //   required: true,
      // },
      // maybe add prop for what to do on login success... just emit, or handle redirect here?
    },
    data: function () {
      return {
        loading: false,
        success: false,  // set to true if/when we know we were successful
        formErrors: [],
        loginFieldErrors: [],
        passwordFieldErrors: [],
        formData: { email: '', password: '' }, // make sure all reactive properties are initialized from the start
        inputIdSuffix: 'input',  // append to field names for input ids
        inputGroupSuffix: 'input-group',  // append to field names for input group ids
        formGroupSuffix: 'form-group',  // append to field names for form group ids
        fieldInfo: {  // NOT intended to be reactive, just a data structure to keep things DRY in the template
          login: {
            name: 'email',  // we use this as the base for ids on all things
            icon: 'envelope',
            label: 'Email',
            placeholder: 'Email',
            autocomplete: 'email',
            type: 'email',
          },
          password: {
            name: 'password',  // we use this as the base for ids on all things
            icon: 'shield-lock',
            label: 'Password',
            placeholder: 'Password',
            autocomplete: 'current-password',
            type: 'password',
          },
        },
      };
    },
    computed: {
      passwordResetUrl() {
        const urlMap = this.$splUrlMap || {};  // from mixin
        return urlMap['password_reset'] || '/accounts/password/reset/';
      }
    },
    mounted: function () {},
    methods: {
      handleLoginFormReset() {
        this.formData.password = '';
        this.formData.email = '';
      },
      async handleLoginFormSubmit(e) {
        e.preventDefault();

        this.loading = true;
        this.$emit('started');

        const sendToLogin = {
          login: this.formData.email,
          password: this.formData.password,
        };

        try {
          //csrfmiddlewaretoken: this.csrfToken, // removed this completely -- our Axios instance will already send the CSRF token as a header (from the csrf cookie)
          //     and we need to make sure we use the one in the cookie so it is always up to date if the user already logged in on another tab!
          //     a user could have multiple tabs open and the token could change, and/or the token in the page HTML could be stale

          const queryString = window.location.search;
          const urlParams = new URLSearchParams(queryString);
          const nextParam = urlParams.get('next');

          if (nextParam) {
            sendToLogin.next = nextParam;
          }

          // FOR TESTING
          // console.log('sendToLogin', sendToLogin, suppliosApi.auth.login);
          // setTimeout(() => {
          //   this.$emit('success', {});
          // }, 1000);

          const response = await suppliosApi.auth.login(sendToLogin);
          if (response?.status === 200 && response?.data?.location?.length > 0) {
            // all good?  then redirect to the location, keep loading true to show loading state
            this.success = true;
            this.$emit('success', response.data);  // set location specifically in this, and let parent handle the redirect?
            //window.location.href = response.data.location;  // let parent handle it
          } else {
            // this should not happen, but if it does, we should know about it
            console.error('Got response from login, but not a 200:', response);
          }
        } catch (error) {
          if (axios.isAxiosError(error)) {
            // this will also catch invalid credential responses (return 400) this will be common, and we don't want to log everything (it includes password)
            const statusCode = error.response?.status;  // 400, 403, etc.
            const originalLoginEmail = sendToLogin.login;
            // allauth sends back nested response data of { form, html }
            // inside form, we have { errors, field_order, fields }  - errors is [string], field_order is [string], fields is object of { field_name: { errors: [string], help_text: string, label: string, value: string we submitted, widget: { attrs: { autocomplete: string, maxlength: int, placeholder: string } } } }
            const formResponseData = error.response?.data?.form || {};  // allauth sends back nested response data of { form, html }
            this.formErrors = formResponseData.errors || [];
            this.loginFieldErrors = formResponseData.fields?.login?.errors || [];
            this.passwordFieldErrors = formResponseData.fields?.password?.errors || [];
            const allErrors = {
              form: this.formErrors,
              login: this.loginFieldErrors,
              password: this.passwordFieldErrors,
            };
            // HANDLE ERRORS - first we handle any 'expected' ones with more limited logs, then we handle anything unexpected with more stuff in logs
            if (statusCode === 400) {
              console.warn(`LOGIN ERROR - status = 400 (likely invalid credentials) - for email/login ${originalLoginEmail}`, allErrors);
            } else {
              console.error(`LOGIN ERROR - status = ${statusCode} - for email/login ${originalLoginEmail}`, allErrors);
            }
          } else {
            console.error('Non-axios error during login', error);
          }
          this.$emit('error', error);
        }
        this.loading = false;  // error or success, we are not loading... if successful, you should have already set this.success=true
        this.$emit('finished');
      },
    },
  };
</script>

<style></style>
